import { Component } from '@theme/component';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { MediaStartedPlayingEvent } from '@theme/events';

/** Only the visible customer clip is fetched; hidden clips retain lightweight posters. */
class SowpeaceUGCCarousel extends Component {
  #abort;
  #observer;
  #ratios = new Map();
  #active;
  #manualPause = new WeakSet();
  #suspended = false;
  #motion;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#ratios.clear();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#motion = matchMedia('(prefers-reduced-motion: reduce)');
    for (const media of this.querySelectorAll('deferred-media')) {
      this.#ratios.set(media, 0);
      const controls = document.createElement('div');
      controls.className = 'ugc-video-controls';
      controls.innerHTML = `<button type="button" class="ugc-media-toggle" data-ugc-play aria-label="${this.dataset.play}"><svg viewBox="0 0 24 24" aria-hidden="true"><path class="ugc-play-path" d="m9 5 10 7-10 7Z"/><path class="ugc-pause-path" d="M8 5v14M16 5v14"/></svg></button><button type="button" class="ugc-media-toggle" data-ugc-mute aria-label="${this.dataset.unmute}" aria-pressed="false"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4Z"/><path class="ugc-muted-path" d="m16 9 6 6m0-6-6 6"/><path class="ugc-audio-path" d="M15 8c3 2 3 6 0 8m3-11c5 4 5 10 0 14"/></svg></button>`;
      media.querySelector('.ugc-video-controls')?.remove();
      media.append(controls);
    }
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) this.#ratios.set(entry.target, entry.intersectionRatio);
      this.#choose();
    }, { threshold: [0, .25, .5, .75, 1] });
    for (const media of this.#ratios.keys()) this.#observer.observe(media);
    this.addEventListener('click', this.#click, { capture: true, signal });
    document.addEventListener('visibilitychange', this.#choose, { signal });
    this.#motion.addEventListener('change', this.#choose, { signal });
    document.addEventListener(DialogOpenEvent.eventName, this.#dialogOpen, { capture: true, signal });
    document.addEventListener(DialogCloseEvent.eventName, this.#dialogClose, { capture: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#release(this.#active);
    this.#active = undefined;
    this.#ratios.clear();
  }

  #dialogOpen = () => { this.#suspended = true; this.#release(this.#active); this.#active = undefined; };
  #dialogClose = () => { this.#suspended = false; this.#choose(); };

  #choose = () => {
    if (document.hidden || this.#suspended || this.#motion.matches || navigator.connection?.saveData) {
      this.#release(this.#active);
      this.#active = undefined;
      return;
    }
    let best = this.#active;
    let ratio = best ? this.#ratios.get(best) || 0 : 0;
    for (const [media, visible] of this.#ratios) {
      if (visible > ratio + .05) { best = media; ratio = visible; }
    }
    if (ratio < .35) best = undefined;
    if (best === this.#active) return;
    this.#release(this.#active);
    this.#active = best;
    if (best && !this.#manualPause.has(best)) this.#play(best);
  };

  #play(media) {
    let video = media.querySelector('video');
    if (!video) {
      const source = media.querySelector('template')?.content.querySelector('video');
      if (!source) return;
      video = source.cloneNode(true);
      video.removeAttribute('autoplay');
      video.muted = true;
      video.loop = true;
      video.controls = false;
      video.playsInline = true;
      video.preload = 'none';
      video.tabIndex = -1;
      media.append(video);
      media.setAttribute('data-media-loaded', 'true');
    }
    media.dispatchEvent(new MediaStartedPlayingEvent(media));
    video.play().then(() => {
      if (this.#active !== media || this.#suspended) { video.pause(); return; }
      media.dataset.ugcPlaying = 'true';
      media.querySelector('.deferred-media__poster-button')?.classList.add('deferred-media__playing');
      media.querySelector('[data-ugc-play]')?.setAttribute('aria-label', this.dataset.pause);
    }).catch(() => {
      media.dataset.ugcPlaying = 'false';
      media.querySelector('[data-ugc-play]')?.setAttribute('aria-label', this.dataset.play);
    });
  }

  #release(media) {
    if (!media) return;
    for (const video of media.querySelectorAll('video')) {
      video.pause();
      video.removeAttribute('src');
      for (const source of video.querySelectorAll('source')) source.removeAttribute('src');
      video.load();
      video.remove();
    }
    media.removeAttribute('data-media-loaded');
    media.dataset.ugcPlaying = 'false';
    media.dataset.ugcAudio = 'false';
    media.querySelector('.deferred-media__poster-button')?.classList.remove('deferred-media__playing');
    media.querySelector('[data-ugc-play]')?.setAttribute('aria-label', this.dataset.play);
    media.querySelector('[data-ugc-mute]')?.setAttribute('aria-label', this.dataset.unmute);
    media.querySelector('[data-ugc-mute]')?.setAttribute('aria-pressed', 'false');
  }

  #click = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-ugc-play], [data-ugc-mute], .deferred-media__poster-button');
    if (!button) return;
    const media = button.closest('deferred-media');
    if (!media || !this.contains(media)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.hasAttribute('data-ugc-mute')) {
      const video = media.querySelector('video');
      if (!video) return;
      video.muted = !video.muted;
      media.dataset.ugcAudio = String(!video.muted);
      button.setAttribute('aria-label', video.muted ? this.dataset.unmute : this.dataset.mute);
      button.setAttribute('aria-pressed', String(!video.muted));
      return;
    }
    const video = media.querySelector('video');
    if (video && !video.paused) {
      this.#manualPause.add(media);
      video.pause();
      media.dataset.ugcPlaying = 'false';
      media.querySelector('[data-ugc-play]')?.setAttribute('aria-label', this.dataset.play);
    } else {
      this.#manualPause.delete(media);
      if (this.#active !== media) this.#release(this.#active);
      this.#active = media;
      this.#play(media);
    }
  };
}

if (!customElements.get('sowpeace-ugc-carousel')) customElements.define('sowpeace-ugc-carousel', SowpeaceUGCCarousel);
